import React from 'react';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import { companyInfo } from '../mockData';
import { QuickQuoteStart } from './QuickQuoteStart';

const Contact = () => {
  return (
    <section id="contact" style={{ backgroundColor: '#edf2f7', padding: '120px 0' }}>
      <div className="container">
        {/* Section Header */}
        <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
            Get a Quote - Entirely Online
          </h2>
          <p className="body-large" style={{ 
            color: 'var(--text-secondary)',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            Tell us what you need and we'll email you an exact quote. We help our California clients execute legal support services across all 50 states.
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '4rem',
          alignItems: 'start'
        }} className="contact-grid">
          
          {/* Contact Form */}
          <div style={{
            backgroundColor: 'var(--bg-page)',
            border: '1px solid var(--border-medium)',
            borderRadius: '12px',
            padding: '2.5rem'
          }}>
            <h3 className="heading-2" style={{ 
              marginBottom: '1.5rem',
              fontSize: '1.5rem'
            }}>
              Start Your Quote
            </h3>

            <QuickQuoteStart />
          </div>

          {/* Contact Information */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
            {/* Contact Details */}
            <div style={{
              backgroundColor: 'var(--bg-page)',
              border: '1px solid var(--border-medium)',
              borderRadius: '12px',
              padding: '2.5rem'
            }}>
              <h3 className="heading-2" style={{ 
                marginBottom: '2rem',
                fontSize: '1.5rem'
              }}>
                Get in Touch
              </h3>

              <div style={{ marginBottom: '2rem' }}>
                {[
                  { icon: Phone, label: 'Phone', value: companyInfo.phone, href: `tel:${companyInfo.phone}` },
                  { icon: Mail, label: 'Email', value: companyInfo.email, href: `mailto:${companyInfo.email}` },
                  { icon: MapPin, label: 'Address', value: `${companyInfo.address}, ${companyInfo.city}, ${companyInfo.state} ${companyInfo.zip}` },
                  { icon: Clock, label: 'Hours', value: companyInfo.hours }
                ].map((item, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '1rem',
                    marginBottom: '1.5rem',
                    paddingBottom: index < 3 ? '1.5rem' : '0',
                    borderBottom: index < 3 ? '1px solid var(--border-light)' : 'none'
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      backgroundColor: 'var(--brand-primary)',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <item.icon size={20} color="white" />
                    </div>
                    <div>
                      <p className="body-small" style={{ 
                        color: 'var(--text-secondary)', 
                        marginBottom: '0.25rem',
                        fontWeight: '600'
                      }}>
                        {item.label}
                      </p>
                      {item.href ? (
                        <a 
                          href={item.href}
                          style={{ 
                            color: 'var(--text-primary)', 
                            textDecoration: 'none',
                            fontWeight: '500'
                          }}
                          onMouseOver={(e) => e.target.style.color = 'var(--brand-primary)'}
                          onMouseOut={(e) => e.target.style.color = 'var(--text-primary)'}
                        >
                          {item.value}
                        </a>
                      ) : (
                        <p style={{ color: 'var(--text-primary)', fontWeight: '500' }}>
                          {item.value}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Google Map Embed */}
              <div style={{ marginTop: '2rem' }}>
                <h4 className="heading-4" style={{ marginBottom: '1rem', color: 'var(--text-primary)' }}>
                  Our Location
                </h4>
                <div style={{
                  width: '100%',
                  height: '300px',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: '1px solid var(--border-medium)'
                }}>
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3297.8!2d-116.5453!3d33.8303!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80db1b0e3b4b4b4b%3A0x1234567890abcdef!2s225%20S%20Civic%20Dr%2C%20Palm%20Springs%2C%20CA%2092262!5e0!3m2!1sen!2sus!4v1234567890123"
                    width="100%"
                    height="300"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Legal Document Specialist Office Location - 225 South Civic Drive, Palm Springs, CA"
                  ></iframe>
                </div>
                <p style={{ 
                  marginTop: '1rem',
                  fontSize: '0.9rem',
                  color: 'var(--text-secondary)',
                  textAlign: 'center'
                }}>
                  Visit our office in the heart of Palm Springs, California
                </p>
              </div>
            </div>

            {/* Business Hours */}
            <div style={{
              backgroundColor: 'var(--bg-page)',
              border: '1px solid var(--border-medium)',
              borderRadius: '12px',
              padding: '2.5rem'
            }}>
              <h3 className="heading-2" style={{ 
                marginBottom: '1.5rem',
                fontSize: '1.5rem'
              }}>
                Business Hours
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span className="body-small" style={{ color: 'var(--text-primary)' }}>Monday - Friday</span>
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>9:00 AM - 5:00 PM</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span className="body-small" style={{ color: 'var(--text-primary)' }}>Saturday</span>
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>Closed</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span className="body-small" style={{ color: 'var(--text-primary)' }}>Sunday</span>
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>Closed</span>
                </div>
                <div style={{ 
                  marginTop: '1rem', 
                  padding: '1rem', 
                  backgroundColor: 'var(--bg-card)', 
                  borderRadius: '8px',
                  border: '1px solid var(--border-medium)'
                }}>
                  <Clock size={16} color="var(--brand-primary)" style={{ display: 'inline-block', marginRight: '0.5rem' }} />
                  <span className="caption" style={{ color: 'var(--text-secondary)' }}>
                    E-Filing services available 24/7
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      <style>{`
        @media (max-width: 767px) {
          .contact-grid {
            grid-template-columns: 1fr !important;
            gap: 2rem !important;
          }
          
          div[style*="gridTemplateColumns: '1fr 1fr'"] {
            grid-template-columns: 1fr !important;
          }
        }
        
        input:focus, select:focus, textarea:focus {
          outline: 2px solid var(--brand-primary);
          outline-offset: 2px;
        }

      `}</style>
    </section>
  );
};

export default Contact;